/**
 * Client state for one run through a connected-app onboarding session: which
 * step the user is on, the whole step list the backend handed us, the result
 * of the step just executed, the OAuth `state` the URL step minted, the
 * session record itself, and whether the run finished.
 *
 * One atom per field, not one atom for the group. Every reader here takes a
 * subset — eleven files read `currentStep`, three read `stepResult`, one reads
 * `oauthState` — and a single atom for the group would re-render all eleven
 * every time a background live search lands a new `stepResult`, re-firing the
 * `useAutoSearch` and `useLoadSectionMappingContext` effects that key off
 * `currentStep` identity.
 *
 * `integrationOnboardingSessionAtom` composes the seven for a reader that
 * wants the whole shape. It is derived, and therefore read-only, so it cannot
 * become a write path back to a single blob.
 *
 * ## No persistence
 *
 * These seven values are in-memory and always were: the state they replace was
 * never in the persisted blob, so there is no legacy key to migrate from and
 * no versioned storage envelope to define. An onboarding session that survives
 * a reload is re-seeded from the backend by `initialiseOnboardingSession`, not
 * read back off disk.
 *
 * They are also not swept on logout, and deliberately: the state they replace
 * was not cleared by either sign-out path, so wiring a reset into
 * `clearLogoutStorage` would be new behaviour rather than a carried-over one.
 * The only resets are the two the product already performs — closing the
 * onboarding dialog and deleting a session.
 *
 * ## Writers
 *
 * Four of the writers below — `setCurrentStepNumber`, `setCurrentStep`,
 * `setAllSteps` and `setIntegrationOnboardingSessionDetails` — have no caller
 * outside this module; the three multi-field functions at the bottom are their
 * whole audience. They stay exported because each was reachable from anywhere
 * before, and narrowing that surface is a separate decision from changing the
 * mechanism that backs it. It is also what lets each field's transition be
 * pinned independently of the functions that compose them, so a later change
 * to one of those sequences reads as a change to the sequence rather than as a
 * field that quietly stopped being written.
 */
import { transformOnboardingStepBase64Entries } from '@/hooks/mutation/integration/direct-backend/onboarding-session/encoding';
import {
  type IntegrationOnboardingStepExecutionResponse,
  type PendingIntegrationOnboardingExecutionStep,
} from '@/types';
import { type CreateIntegrationOnboardingSessionResponse } from '@heidi/integrations/connected-apps/contracts';
import { StepType } from '@heidi/schema/ml-scribe';
import { atom, getDefaultStore } from 'jotai';

export type IntegrationOnboardingSessionState = {
  currentStepNumber: number | null;
  currentStep: PendingIntegrationOnboardingExecutionStep | null;
  allSteps: PendingIntegrationOnboardingExecutionStep[];
  stepResult: { [key: string]: unknown } | null;
  oauthState: string | null;
  integrationOnboardingSessionDetails: CreateIntegrationOnboardingSessionResponse | null;
  onboardingFinished: boolean;
};

export type IntegrationOnboardingSessionField = keyof IntegrationOnboardingSessionState;

const INITIAL_STATE: IntegrationOnboardingSessionState = {
  currentStepNumber: 0,
  currentStep: null,
  allSteps: [],
  stepResult: null,
  oauthState: null,
  integrationOnboardingSessionDetails: null,
  onboardingFinished: false,
};

export const currentStepNumberAtom = atom<number | null>(INITIAL_STATE.currentStepNumber);
export const currentStepAtom = atom<PendingIntegrationOnboardingExecutionStep | null>(
  INITIAL_STATE.currentStep
);
export const allStepsAtom = atom<PendingIntegrationOnboardingExecutionStep[]>(
  INITIAL_STATE.allSteps
);
export const stepResultAtom = atom<{ [key: string]: unknown } | null>(
  INITIAL_STATE.stepResult
);
export const oauthStateAtom = atom<string | null>(INITIAL_STATE.oauthState);
export const integrationOnboardingSessionDetailsAtom =
  atom<CreateIntegrationOnboardingSessionResponse | null>(
    INITIAL_STATE.integrationOnboardingSessionDetails
  );
export const onboardingFinishedAtom = atom<boolean>(INITIAL_STATE.onboardingFinished);

/** The whole shape, for a reader that wants more than a field. */
export const integrationOnboardingSessionAtom = atom<IntegrationOnboardingSessionState>(
  (get) => ({
    currentStepNumber: get(currentStepNumberAtom),
    currentStep: get(currentStepAtom),
    allSteps: get(allStepsAtom),
    stepResult: get(stepResultAtom),
    oauthState: get(oauthStateAtom),
    integrationOnboardingSessionDetails: get(integrationOnboardingSessionDetailsAtom),
    onboardingFinished: get(onboardingFinishedAtom),
  })
);

export function setCurrentStepNumber(currentStepNumber: number | null): void {
  getDefaultStore().set(currentStepNumberAtom, currentStepNumber);
}

export function setCurrentStep(
  currentStep: PendingIntegrationOnboardingExecutionStep | null
): void {
  getDefaultStore().set(currentStepAtom, currentStep);
}

export function setAllSteps(allSteps: PendingIntegrationOnboardingExecutionStep[]): void {
  getDefaultStore().set(allStepsAtom, allSteps);
}

export function setStepResult(stepResult: { [key: string]: unknown } | null): void {
  getDefaultStore().set(stepResultAtom, stepResult);
}

export function setOauthState(oauthState: string | null): void {
  getDefaultStore().set(oauthStateAtom, oauthState);
}

/**
 * NOT a plain field write: the payload goes through
 * `transformOnboardingStepBase64Entries` on the way in, so every reader of
 * `integrationOnboardingSessionDetails` sees decoded step payloads and no
 * reader has to decode for itself. A null payload short-circuits — the
 * transform is never handed one.
 */
export function setIntegrationOnboardingSessionDetails(
  integrationOnboardingSessionDetails: CreateIntegrationOnboardingSessionResponse | null
): void {
  getDefaultStore().set(
    integrationOnboardingSessionDetailsAtom,
    integrationOnboardingSessionDetails
      ? transformOnboardingStepBase64Entries(integrationOnboardingSessionDetails)
      : null
  );
}

export function setIntegrationOnboardingFinished(onboardingFinished: boolean): void {
  getDefaultStore().set(onboardingFinishedAtom, onboardingFinished);
}

type Store = ReturnType<typeof getDefaultStore>;

/**
 * One reset per field, keyed by field so a new field is a compile error here
 * rather than a value that silently survives a clear.
 */
const RESET_BY_FIELD: Record<IntegrationOnboardingSessionField, (store: Store) => void> =
  {
    currentStepNumber: (store) =>
      store.set(currentStepNumberAtom, INITIAL_STATE.currentStepNumber),
    currentStep: (store) => store.set(currentStepAtom, INITIAL_STATE.currentStep),
    allSteps: (store) => store.set(allStepsAtom, INITIAL_STATE.allSteps),
    stepResult: (store) => store.set(stepResultAtom, INITIAL_STATE.stepResult),
    oauthState: (store) => store.set(oauthStateAtom, INITIAL_STATE.oauthState),
    integrationOnboardingSessionDetails: (store) =>
      store.set(
        integrationOnboardingSessionDetailsAtom,
        INITIAL_STATE.integrationOnboardingSessionDetails
      ),
    onboardingFinished: (store) =>
      store.set(onboardingFinishedAtom, INITIAL_STATE.onboardingFinished),
  };

const ALL_FIELDS = Object.keys(RESET_BY_FIELD) as IntegrationOnboardingSessionField[];

/**
 * Every field back to its initial value, unconditionally. The reducer this
 * replaces was declared with no parameters at all, so it could not read the
 * state it replaced: `currentStepNumber` goes back to 0 — not null, and not 1.
 */
export function clearIntegrationOnboardingSession(): void {
  const store = getDefaultStore();
  ALL_FIELDS.forEach((field) => RESET_BY_FIELD[field](store));
}

/**
 * Rewinds one step, resolving the step out of the list the session was seeded
 * with. Guarded on `currentStepNumber > 1` — the initial 0 and a null are both
 * falsy and both mean "nowhere to rewind to".
 */
export function goToPreviousStep(): void {
  const store = getDefaultStore();
  const currentStepNumber = store.get(currentStepNumberAtom);
  const allSteps = store.get(allStepsAtom);
  if (currentStepNumber && currentStepNumber > 1) {
    const newStepNumber = currentStepNumber - 1;
    const newStep = allSteps.at(newStepNumber - 1) ?? null;
    setCurrentStepNumber(newStepNumber);
    setCurrentStep(newStep);
  }
}

export function initialiseOnboardingSession({
  data,
}: {
  data: CreateIntegrationOnboardingSessionResponse;
}): void {
  let stepNumber = data.current_step_number || 1;
  const allSteps = data.steps as PendingIntegrationOnboardingExecutionStep[];
  let currentStep = allSteps.at(stepNumber - 1) ?? allSteps.at(0) ?? null;

  // Sessions resumed mid-OAuth (stuck on the code-exchange step) can't be
  // continued — the exchange step validates the callback against an
  // `oauthState` value set by the URL step in the same tab, and that state
  // is wiped by the clear below. Rewind to the URL step so the user
  // re-triggers the consent flow instead of waiting on a callback that
  // will never arrive. Defensive: only rewind when the preceding step is
  // actually the URL step — if the backend ever inserts a form/search
  // step between URL and exchange, blind decrement would drop the user on
  // the wrong UI.
  if (
    currentStep?.step_type === StepType.oauth_authorization_code_exchange &&
    stepNumber > 1 &&
    allSteps.at(stepNumber - 2)?.step_type === StepType.oauth_authorization_code_url
  ) {
    stepNumber -= 1;
    currentStep = allSteps.at(stepNumber - 1) ?? currentStep;
  }

  clearIntegrationOnboardingSession();
  setIntegrationOnboardingSessionDetails(data);
  setCurrentStepNumber(stepNumber);
  setAllSteps(allSteps);
  setCurrentStep(currentStep);
}

/**
 * Advances to the step the execution response names. The step itself is
 * resolved out of the state's OWN `allSteps` — read before the writes below,
 * and deliberately not out of `data.updated_session.steps`, so a backend
 * reordering cannot move the user mid-run.
 */
export function updateOnboardingSession({
  data,
}: {
  data: IntegrationOnboardingStepExecutionResponse;
}): void {
  const allSteps = getDefaultStore().get(allStepsAtom);

  setCurrentStepNumber(data.updated_session.current_step_number);
  setCurrentStep(
    data.updated_session.current_step_number
      ? (allSteps.at(data.updated_session.current_step_number - 1) ?? null)
      : null
  );
  setIntegrationOnboardingSessionDetails(data.updated_session);
  setStepResult(data.step_result);
}
